import { Controller } from '@hotwired/stimulus';

import {
  holidayDatesText,
  isValidIsoDate,
  isoParts,
  isoWeekday,
  interpolate,
  NOTE_SEPARATOR,
  periodDateErrorKey,
  periodDates,
  pluralize,
} from '../utils/planning_calendar_date_formats.js';
import { ALSACE_MOSELLE_HOLIDAY_KEYS, HOLIDAY_KEYS } from '../utils/planning_french_holidays.js';
import { isInteractiveTarget } from '../utils/interactive_target.js';
import { anchorEndOnStart } from '../utils/planning_period_end_anchor.js';
import { periodErrors } from '../utils/planning_period_errors.js';
import { DEFAULT_WEEKDAYS, workCalendar } from '../utils/planning_work_calendar.js';

const ID_PLACEHOLDER = '__ID__';
// Champs postés qui ne sont pas des réglages : exclus de la comparaison d'états (sale / propre).
const VOLATILE_FIELDS = new Set(['calendar_fingerprint', 'authenticity_token', '_method']);
// Libellé d'un brouillon pas encore saisi : le texte que le modèle de ligne rend (« Nouvelle période »).
const LABEL_NODE = '[data-period-label]';

const ROW_ATTRIBUTE = 'data-plan-settings-period';
// Ligne NOUVELLE pas encore confirmée par « Ajouter la période » (planche B1·d) : ses champs ne partent pas.
const DRAFT_ATTRIBUTE = 'data-plan-settings-draft';
const STASHED_NAME_ATTRIBUTE = 'data-plan-settings-name';
const HINT_ATTRIBUTE = 'data-plan-settings-hint';
const DRAFT_ACTIONS_SELECTOR = '[data-plan-settings-draft-actions]';
const COMMIT_SELECTOR = '[data-action~="plan-settings#commitDraft"]';
const EDIT_SELECTOR = '[aria-controls][data-action~="plan-settings#edit"]';
// Gabarit brut du nom du crayon (« Modifier « %{label} » »), rendu par le serveur sur le crayon lui-même.
const EDIT_LABEL_ATTRIBUTE = 'data-plan-settings-edit-label';
const ADD_SELECTOR = '[data-action~="plan-settings#add"]';
const LOST_SELECTOR = '[data-plan-settings-lost]';
const HOLIDAY_SELECTOR = '[data-plan-settings-holiday]';
const STATUS_TEXT_SELECTOR = '[data-plan-settings-status-text]';
const STATUS_DOT_SELECTOR = '[data-plan-settings-status-dot]';
const DATE_PICKER_ROOT = '[data-controller~="date-picker"]';
const DATE_PICKER_DISPLAY = '[data-date-picker-target~="display"]';

const PERIODS_COUNT_ID = 'plan_periods_count';
const HOLIDAYS_COUNT_ID = 'plan_holidays_count';
const HOLIDAYS_SUMMARY_ID = 'plan_holidays_summary';

/**
 * plan-settings — tiroir « Réglages » du planning (planches B1·a–i ; plan
 * `docs/superpowers/plans/2026-10-29-jours-non-travailles.md`, T5). Le tiroir n'enregistre rien avant
 * « Enregistrer » (PATCH du formulaire rendu par le serveur, Turbo) ; ce contrôleur ne fait AUCUNE requête.
 *
 * À chaque changement (pastille Sam / Dim, interrupteur de férié, champ de période, ajout, suppression), tout
 * est recalculé sur place :
 *   • le calendrier de comptage = masque des pastilles (lun-ven du masque stocké + Sam / Dim cochés) + fériés
 *     selon la règle de fusion RF-T1 du serveur : si les fériés affichés cochés sont exactement les fériés
 *     stockés parmi les affichés, la valeur stockée reste telle quelle (`null` reste `null`, fériés non
 *     appliqués) ; sinon (stockés − affichés) ∪ cochés. SANS les périodes (`workCalendar`) ;
 *   • par ligne : « N j » et phrase longue (`lost_days` / `lost_days_during` quand la période déborde du
 *     chantier) = jours ouvrés de la période BORNÉE à l'étendue, rien quand les dates sont refusées par le
 *     concern (années, 366 jours) ; « · finit après le chantier » si elle finit après ; dates « 2 → 20 août »
 *     (année omise quand c'est `displayYear`) ; nom du crayon (`aria-label`, `title`) = gabarit brut
 *     `data-plan-settings-edit-label` + libellé affiché ;
 *   • par férié : la note `#plan_holiday_<clé>_note` entière, dates de `data-dates` (« lun. 29 mars »,
 *     « + N autres » au-delà de trois) puis le motif sur la première occurrence (« déjà un week-end » hors du
 *     masque, chômé ou travaillé dès que les fériés sont appliqués, `PlanSettingsView#holiday_motif` ;
 *     « pendant « X » », « travaillé sur ce chantier ») ; aucun motif tant que les fériés que posterait le
 *     formulaire valent `null` (Q2). Cocher un férié d'un planning `null` rend les fériés appliqués (le
 *     serveur enregistrera les cochés) : les autres affichent alors « travaillé sur ce chantier » ;
 *   • compteurs (`#plan_periods_count` hors brouillons, `#plan_holidays_count`), résumé des fériés, état vide.
 *
 * Validation d'une ligne, mêmes règles et mêmes textes que `NonWorkingCalendar` (T2) : dates complètes
 * (`invalid_dates`), fin ≥ début, années 2000-2100, 366 jours au plus, puis aucun chevauchement avec une
 * autre ligne NI avec une période cachée (`hiddenPeriods`, Q4). Comme le serveur (ordre RF-T1 : cachées,
 * lignes inchangées, lignes modifiées ou nouvelles, puis le brouillon), la ligne la plus récente porte
 * l'erreur et nomme la première, par début, qu'elle chevauche. Erreur posée par `textContent` dans
 * `#plan_period_<id>_errors` (jamais de HTML, #591) ; `aria-invalid` sur Du / Au ; l'éditeur s'ouvre.
 * Un brouillon sans dates n'affiche pas d'erreur : « Ajouter la période » reste désactivé, décrit par l'aide.
 *
 * Brouillon (B1·d) : « Ajouter » clone le `<template>` (`__ID__` → `crypto.randomUUID()` dans tous les
 * attributs), l'ajoute en fin de liste, éditeur ouvert, focus sur Libellé. Ses champs ne partent pas : leur
 * `name` est mis de côté (`data-plan-settings-name`), hors du corps posté comme un champ `disabled`, mais
 * saisissables (un champ `disabled` ne se tape pas ; un modèle rendu `disabled` est réactivé). « Ajouter la
 * période » rend les noms, retire le bloc `[data-plan-settings-draft-actions]` et ferme l'éditeur ;
 * « Annuler » retire la ligne. Un seul brouillon à la fois.
 *
 * Échap (QA E1 DS-6) : dans une ligne ouverte, il la referme (focus au crayon) ; dans un brouillon, il ne fait
 * rien. Consommé (`preventDefault`) dans les deux cas, pour que le tiroir ne se ferme pas sur la saisie.
 *
 * Sale / propre : l'instantané des paires (nom, valeur) que le formulaire posterait (champs activés, cases
 * cochées, hors `calendar_fingerprint`, `authenticity_token`, `_method`), pris au `connect`, est comparé à
 * chaque changement. « Enregistrer » est désactivé si rien n'a changé OU si une erreur est affichée (ligne
 * ou brouillon, B1·i). Statut du pied par `textContent` (`unsaved` / `no_changes`), point montré par `hidden`.
 *
 * Réponses 422 / 409 : des flux Turbo remplacent `#plan_settings_alert` et `#plan_period_<id>_errors` ;
 * après chaque rendu de flux, `aria-invalid`, éditeurs ouverts, « Ajouter la période » et « Enregistrer »
 * sont resynchronisés. Turbo réactive le bouton au `turbo:submit-end` : resynchronisé aussi.
 *
 * Aucune couleur ni icône écrites ici (contrat #18) : classes et icônes rendues par le serveur, `hidden`.
 * Dates en chaînes ISO, minuit UTC (pré-mortem 11).
 *
 * Targets :
 *   • weekday        — chaque pastille Sam / Dim (`<label>` qui contient la case `plan_settings[saturday]`
 *                      ou `[sunday]`), action `change->plan-settings#refresh`
 *   • periodList     — la liste des lignes `id="plan_period_<id>"` `data-plan-settings-period="<id>"`
 *   • periodTemplate — `<template>` de la ligne, `__ID__` à la place de l'id
 *   • empty          — l'état vide (B1·e)
 *   • status         — statut du pied (`role="status"`) : `[data-plan-settings-status-text]` +
 *                      `[data-plan-settings-status-dot]`
 *   • submit         — « Enregistrer »
 *
 * Values :
 *   • calendar      (Object) — calendrier STOCKÉ `{ weekdays, holidays, periods }`
 *   • extent        (Object) — `{ start, end }` ISO ou `null` / absent (aucun lot daté)
 *   • today         (String) — ISO (lu par le serveur pour choisir les lignes ; non utilisé ici)
 *   • displayYear   (Number) — année omise dans les dates, `null` / absent sinon
 *   • hiddenPeriods (Array)  — `[{ id, label, start, end, range }]`
 *   • messages      (Object) — tous les textes (`Plans::PlanSettingsView#messages`) : `invalid_dates`,
 *                     `overlap` (brut), `date_range` `{ forms, month_names }`, `too_long`, `out_of_years`,
 *                     `end_before_start`, `period_formats`, `short_weekdays`, `short_months`, `days_short`,
 *                     `ends_after`, `lost_days`, `lost_days_during`, `weekend_note`, `period_note`,
 *                     `worked_note`, `more`, `holidays_summary`, `off`, `worked`, `none_off`, `unsaved`,
 *                     `no_changes`, `default_label` ; `label_too_long` facultatif
 *
 * Actions : `refresh` (pastilles, interrupteurs), `add`, `edit` (crayon, `aria-controls`), `remove`,
 *   `commitDraft`, `cancelDraft`. Les champs de période (Libellé, Du / Au du sélecteur de date) sont écoutés
 *   sur l'élément (`input` / `change`) : aucune action à poser sur eux.
 *
 * @example
 * <div data-controller="plan-settings" data-plan-settings-extent-value='{"start":"2027-03-01","end":"2027-12-17"}'
 *      data-plan-settings-messages-value='{"days_short":"%{count} j", …}'>
 *   <form>…
 *     <label data-plan-settings-target="weekday" data-action="change->plan-settings#refresh">
 *       <input type="checkbox" name="plan_settings[saturday]" value="1"> Sam</label>
 *     <ul data-plan-settings-target="periodList">
 *       <li id="plan_period_p1" data-plan-settings-period="p1">…</li>
 *     </ul>
 *     <button type="submit" data-plan-settings-target="submit" disabled>Enregistrer</button>
 *   </form>
 * </div>
 */
export default class extends Controller {
  static targets = ['weekday', 'periodList', 'periodTemplate', 'empty', 'status', 'submit'];

  static values = {
    calendar: Object,
    extent: Object,
    today: String,
    displayYear: Number,
    hiddenPeriods: Array,
    messages: Object,
  };

  connect() {
    this.connected = true;
    this.draftLabels = new WeakMap();
    this.initialPeriods = new Map(
      this.#rows().map((row) => [this.#rowId(row), this.#periodOf(row)])
    );
    this.initialState = this.#formState();

    this.onInput = this.handleInput.bind(this);
    this.onChange = this.handleChange.bind(this);
    this.onSubmitEnd = this.handleSubmitEnd.bind(this);
    this.onBeforeStreamRender = this.handleBeforeStreamRender.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('change', this.onChange);
    // Sur l'élément : il passe AVANT l'écoute du tiroir sur `document` (`slide_over_controller.js#onKeydown`),
    // qui ignore un Échap déjà consommé.
    this.element.addEventListener('keydown', this.onKeydown);
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd);
    // Les flux Turbo sont insérés hors de cet élément : on les écoute sur `document`.
    document.addEventListener('turbo:before-stream-render', this.onBeforeStreamRender);

    this.#syncInvalid();
    this.#syncSubmit();
  }

  disconnect() {
    this.connected = false;
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('change', this.onChange);
    this.element.removeEventListener('keydown', this.onKeydown);
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd);
    document.removeEventListener('turbo:before-stream-render', this.onBeforeStreamRender);
  }

  // ── Recalcul ──────────────────────────────────────────────────────────────────────────────────────────────

  /** Pastilles, interrupteurs : tout ce qui dépend du calendrier de comptage, puis l'état du pied. */
  refresh() {
    const calendar = this.#countingCalendar();
    this.#rows().forEach((row) => this.#paintRow(row, calendar));
    this.#paintHolidays();
    this.#paintCounts();
    this.#refreshEmpty();
    this.#syncSubmit();
  }

  // Un champ de période a changé, ou une ligne est née / partie : validation de toutes les lignes, puis recalcul.
  #periodsChanged() {
    this.#validateRows();
    this.refresh();
  }

  handleInput(event) {
    this.#fieldEvent(event);
  }

  handleChange(event) {
    this.#fieldEvent(event);
  }

  #fieldEvent(event) {
    const field = event?.target;
    if (!field || !(field.getAttribute?.('name') || field.getAttribute?.(STASHED_NAME_ATTRIBUTE))) {
      return; // l'affichage JJ/MM/AAAA du sélecteur de date, sans nom : son champ ISO suivra
    }
    const row = this.#rowOf(field);
    if (!row) {
      this.refresh();
      return;
    }
    anchorEndOnStart(this.#field(row, 'end'), this.#field(row, 'start')?.value);
    this.#periodsChanged();
  }

  // Calendrier des « N j » : masque des pastilles + fériés que posterait le formulaire, sans périodes.
  #countingCalendar() {
    return workCalendar(this.#weekdays(), { holidays: this.#effectiveHolidays() });
  }

  // Lun-ven du masque stocké (jamais modifiés par le tiroir) + Sam (6) / Dim (0) cochés.
  #weekdays() {
    const stored = this.#storedCalendar().weekdays ?? DEFAULT_WEEKDAYS;
    const days = new Set(stored.filter((day) => day >= 1 && day <= 5));
    const boxes = this.weekdayTargets.flatMap((target) =>
      target.matches('input')
        ? [target]
        : Array.from(target.querySelectorAll('input[type="checkbox"]'))
    );
    if (boxes.length === 0)
      stored.filter((day) => day === 0 || day === 6).forEach((d) => days.add(d));
    boxes.forEach((box) => {
      const name = box.getAttribute('name') || '';
      if (!box.checked) return;
      if (name.endsWith('[saturday]')) days.add(6);
      if (name.endsWith('[sunday]')) days.add(0);
    });
    return [...days].sort((a, b) => a - b);
  }

  // Règle de fusion RF-T1 (`Plans::PlanSettingsForm`), pour compter ce que le serveur enregistrerait.
  #effectiveHolidays() {
    const stored = this.#storedCalendar().holidays;
    const base = Array.isArray(stored) ? stored : [];
    const rows = this.#holidayRows();
    const shown = new Set(rows.map((row) => row.key));
    const checked = new Set(rows.filter((row) => row.off).map((row) => row.key));
    const baseShown = new Set(base.filter((key) => shown.has(key)));
    const unchanged =
      baseShown.size === checked.size && [...checked].every((key) => baseShown.has(key));
    if (unchanged) return Array.isArray(stored) ? stored : null;
    return HOLIDAY_KEYS.filter(
      (key) => (base.includes(key) && !shown.has(key)) || checked.has(key)
    );
  }

  #paintRow(row, calendar) {
    const messages = this.#messages();
    const { label, start, end } = this.#periodOf(row);
    const draft = this.#isDraft(row);
    const extent = this.#extent();
    const datesValid = isValidIsoDate(start) && isValidIsoDate(end);
    const ordered = datesValid && start <= end;

    let shownLabel = label.trim() ? label : (messages.default_label ?? '');
    if (draft && !label.trim()) shownLabel = this.draftLabels.get(row) ?? shownLabel;

    // Date manquante : ligne vide, comme l'onglet agence ; « Choisissez les dates » n'est dit qu'une fois, près
    // d'« Ajouter la période » (DS-3, 30/10). Toujours avec l'année, comme `Plans::PlanSettingsView#period_row`.
    const dates = ordered ? periodDates(isoParts(start), isoParts(end), null, messages) : '';

    let days = '';
    let lost = '';
    let endsAfter = false;
    // Dates refusées par le concern (années, 366 jours) : aucun compte, le message d'erreur suffit.
    if (periodDateErrorKey(start, end) === null) {
      const from = extent && start < extent.start ? extent.start : start;
      const to = extent && end > extent.end ? extent.end : end;
      const count = from <= to ? calendar.workedDaysBetween(from, to) : 0;
      // Comme `PlanSettingsView#lost_days_text` : la variante « pendant le chantier » quand elle finit après.
      endsAfter = Boolean(extent) && end > extent.end;
      days = pluralize(messages.days_short, count);
      lost = pluralize(endsAfter ? messages.lost_days_during : messages.lost_days, count);
    }
    const afterText = endsAfter ? (messages.ends_after ?? '') : '';

    this.#setText(row.querySelector(LABEL_NODE), shownLabel);
    this.#syncEditName(row, shownLabel);
    this.#setText(row.querySelector('[data-period-days]'), days);
    this.#setText(row.querySelector(LOST_SELECTOR), lost);
    // « · finit après le chantier » : dans son nœud s'il existe (montré / caché), sinon à la suite des dates,
    // comme `PlanSettingsView#period_summary_dates`.
    const after = row.querySelector('[data-period-ends-after]');
    if (after) {
      this.#setText(after, afterText);
      after.hidden = !endsAfter;
    }
    const datesText = !after && afterText ? [dates, afterText].filter(Boolean).join(' ') : dates;
    this.#setText(row.querySelector('[data-period-dates]'), datesText);
  }

  // Nom du crayon = gabarit du serveur + libellé AFFICHÉ de la ligne, par attributs (jamais d'HTML).
  #syncEditName(row, label) {
    const button = this.#editButtonOf(row);
    const template = button?.getAttribute(EDIT_LABEL_ATTRIBUTE);
    if (!template || !String(label).trim()) return;
    const name = interpolate(template, { label });
    if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
    if (button.getAttribute('title') !== name) button.setAttribute('title', name);
  }

  #paintHolidays() {
    const messages = this.#messages();
    const rows = this.#holidayRows();
    const applied = this.#effectiveHolidays() !== null;
    const mask = this.#weekdays();
    const periods = this.#activePeriods().sort((a, b) => (a.start < b.start ? -1 : 1));
    const year = this.#displayYear();

    // La note entière (« sam. 1 mai · déjà un week-end ») : dates de `data-dates`, puis le motif.
    rows.forEach(({ key, off, dates }) => {
      const valid = dates.filter((iso) => isValidIsoDate(iso));
      const workday = valid.find((iso) => mask.includes(isoWeekday(iso)));
      let reason = '';
      // Jumeau de `PlanSettingsView#holiday_motif` : « déjà un week-end » si TOUTES les dates tombent hors du masque
      // (30/10), chômé OU travaillé dès que les fériés sont appliqués (QA-JNT-27 : l'Assomption décochée un dimanche
      // n'est pas « travaillée ») ; sinon le motif suit la première date en semaine.
      if ((off || applied) && valid.length > 0 && !workday) {
        reason = messages.weekend_note ?? '';
      } else if (off && workday) {
        const period = periods.find((p) => p.start <= workday && workday <= p.end);
        if (period) reason = interpolate(messages.period_note, { label: period.displayLabel });
      } else if (!off && applied) {
        reason = messages.worked_note ?? '';
      }
      const note = [holidayDatesText(dates, year, messages), reason].filter(Boolean);
      this.#setText(this.#byId(`plan_holiday_${key}_note`), note.join(NOTE_SEPARATOR));
    });

    // Même règle que l'agence, sur les fériés AFFICHÉS : chômés (nationaux et Alsace-Moselle) + nationaux
    // travaillés ; un férié d'Alsace-Moselle travaillé est le défaut et ne se compte pas.
    const offCount = rows.filter((row) => row.off).length;
    const workedCount = rows.filter(
      (row) => !row.off && !ALSACE_MOSELLE_HOLIDAY_KEYS.includes(row.key)
    ).length;
    this.#setText(this.#byId(HOLIDAYS_COUNT_ID), String(offCount + workedCount));
    this.#setText(
      this.#byId(HOLIDAYS_SUMMARY_ID),
      offCount === 0
        ? (messages.none_off ?? '')
        : interpolate(messages.holidays_summary, {
            off: pluralize(messages.off, offCount),
            worked: pluralize(messages.worked, workedCount),
          })
    );
  }

  #paintCounts() {
    const committed = this.#rows().filter((row) => !this.#isDraft(row)).length;
    this.#setText(this.#byId(PERIODS_COUNT_ID), String(committed));
  }

  #refreshEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.#rows().length > 0;
  }

  // ── Validation ────────────────────────────────────────────────────────────────────────────────────────────

  #validateRows() {
    const messages = this.#messages();
    const rows = this.#rows();
    const entry = (row) => {
      const period = this.#periodOf(row);
      return { row, ...period };
    };
    const hidden = (this.hasHiddenPeriodsValue ? this.hiddenPeriodsValue : []).map((period) => ({
      label: String(period.label ?? ''),
      start: period.start,
      end: period.end,
      range: period.range,
    }));
    const committed = rows.filter((row) => !this.#isDraft(row)).map(entry);
    const unchanged = committed.filter((item) => this.#isUnchanged(item));
    const changed = committed.filter((item) => !this.#isUnchanged(item));
    const drafts = rows
      .filter((row) => this.#isDraft(row))
      .map((row) => ({ ...entry(row), draft: true }));
    const order = [...hidden, ...unchanged, ...changed, ...drafts];

    const errors = periodErrors(order, messages);
    order.forEach((item, index) => {
      if (item.row) this.#setText(this.#errorNode(item.row), errors[index]);
    });
    this.#syncInvalid();
  }

  #isUnchanged({ row, label, start, end }) {
    const initial = this.initialPeriods.get(this.#rowId(row));
    return (
      Boolean(initial) && initial.label === label && initial.start === start && initial.end === end
    );
  }

  #hasError(row) {
    return Boolean(this.#errorNode(row)?.textContent.trim());
  }

  // Du / Au en erreur tant que le nœud d'erreurs de la ligne n'est pas vide (posé ici ou par un flux 422) ;
  // une ligne en erreur a son éditeur ouvert.
  #syncInvalid() {
    this.#rows().forEach((row) => {
      const invalid = this.#hasError(row);
      ['start', 'end'].forEach((bound) => {
        const display = this.#displayOf(row, bound);
        if (!display) return;
        if (invalid) display.setAttribute('aria-invalid', 'true');
        else display.removeAttribute('aria-invalid');
      });
      if (invalid) this.#openEditor(row);
      if (this.#isDraft(row)) this.#syncDraftActions(row);
    });
  }

  // « Ajouter la période » désactivé tant qu'une date manque ou qu'une erreur est affichée ; décrit par
  // l'erreur, ou par l'aide (le nœud de son `aria-describedby` rendu, rempli de `invalid_dates` s'il est vide).
  #syncDraftActions(row) {
    const commit = row.querySelector(COMMIT_SELECTOR);
    if (!commit) return;
    const { start, end } = this.#periodOf(row);
    const missing = !isValidIsoDate(start) || !isValidIsoDate(end);
    const errored = this.#hasError(row);
    commit.disabled = missing || errored;

    if (!commit.hasAttribute(HINT_ATTRIBUTE)) {
      commit.setAttribute(HINT_ATTRIBUTE, commit.getAttribute('aria-describedby') || '');
    }
    const hintId = commit.getAttribute(HINT_ATTRIBUTE);
    const hint = hintId ? row.querySelector(`[id="${hintId}"]`) : null;
    if (hint) {
      if (!hint.textContent.trim()) hint.textContent = this.#messages().invalid_dates ?? '';
      hint.hidden = !missing;
    }
    const reason = errored ? this.#errorNode(row)?.id : missing ? hintId : '';
    if (reason) commit.setAttribute('aria-describedby', reason);
    else commit.removeAttribute('aria-describedby');
  }

  // ── Lignes ────────────────────────────────────────────────────────────────────────────────────────────────

  add() {
    const pending = this.#rows().find((row) => this.#isDraft(row));
    if (pending) {
      this.#openEditor(pending, { focus: true });
      return;
    }
    const row = this.#cloneRow();
    if (!row) return;
    row.setAttribute(DRAFT_ATTRIBUTE, '');
    this.draftLabels.set(row, row.querySelector(LABEL_NODE)?.textContent ?? '');
    this.#setDraftFields(row, true);
    this.periodListTarget.appendChild(row);
    this.#openEditor(row, { focus: true });
    this.#periodsChanged();
  }

  commitDraft(event) {
    const row = this.#rowOf(event?.currentTarget);
    if (!row || !this.#isDraft(row)) return;
    this.#validateRows();
    const { start, end } = this.#periodOf(row);
    if (!isValidIsoDate(start) || !isValidIsoDate(end) || this.#hasError(row)) {
      this.#syncDraftActions(row);
      return;
    }
    row.removeAttribute(DRAFT_ATTRIBUTE);
    this.draftLabels.delete(row);
    this.#setDraftFields(row, false);
    row.querySelectorAll(DRAFT_ACTIONS_SELECTOR).forEach((node) => node.remove());
    this.#closeEditor(row);
    this.#placeInOrder(row);
    this.#editButtonOf(row)?.focus();
    this.#periodsChanged();
  }

  // DS-ÉCARTS 7 (Nicolas, 30/10) : la période validée va à sa place par date de début dès « Ajouter la période »,
  // comme dans l'onglet agence (`agency_calendar_controller.js#placeInOrder`), sans attendre la liste triée que
  // rend « Enregistrer ». AVANT le focus : déplacer un nœud qui porte le focus le lui fait perdre.
  #placeInOrder(row) {
    const { start } = this.#periodOf(row);
    const next =
      this.#rows().find((other) => other !== row && this.#periodOf(other).start > start) ?? null;
    if (next !== row.nextElementSibling) this.periodListTarget.insertBefore(row, next);
  }

  cancelDraft(event) {
    const row = this.#rowOf(event?.currentTarget) ?? this.#rows().find((r) => this.#isDraft(r));
    if (!row || !this.#isDraft(row)) return;
    row.remove();
    this.#focusAfterRemoval(null);
    this.#periodsChanged();
  }

  // Échap dans une ligne ouverte la referme et rend le focus à son crayon, comme l'onglet agence
  // (`agency_calendar_controller.js#escapeRow`) ; dans un brouillon il ne fait rien. Consommé dans les deux cas :
  // le tiroir ne se ferme pas en jetant la saisie non enregistrée (QA E1 DS-6). Ligne fermée, ou focus hors
  // d'une ligne : Échap ferme le tiroir, comme « Annuler ».
  handleKeydown(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    const row = this.#rowOf(event.target);
    const editor = row && this.#editorOf(row);
    if (!editor || (editor.hidden && !this.#isDraft(row))) return;
    event.preventDefault();
    if (this.#isDraft(row)) return;
    this.#closeEditor(row);
    this.#editButtonOf(row)?.focus();
  }

  // Clic sur la ligne résumée (retour de Nicolas, 30/10) : comme le crayon, qui reste le bouton du clavier et des
  // lecteurs d'écran (un clic SUR lui garde sa propre action). Un brouillon n'a pas de crayon : rien.
  editFromRow(event) {
    if (isInteractiveTarget(event, event.currentTarget)) return;
    const row = this.#rowOf(event.currentTarget);
    if (!row || this.#isDraft(row)) return;
    const pencil = this.#editButtonOf(row);
    if (pencil) this.edit({ currentTarget: pencil });
  }

  edit(event) {
    const row = this.#rowOf(event?.currentTarget);
    const editor = row && this.#editorOf(row);
    if (!editor) return;
    if (editor.hidden) this.#openEditor(row, { focus: true });
    else this.#closeEditor(row);
  }

  remove(event) {
    const row = this.#rowOf(event?.currentTarget);
    if (!row) return;
    const rows = this.#rows();
    const index = rows.indexOf(row);
    const neighbour = rows[index + 1] ?? rows[index - 1] ?? null;
    row.remove();
    this.#focusAfterRemoval(neighbour);
    this.#periodsChanged();
  }

  #cloneRow() {
    if (!this.hasPeriodTemplateTarget || !this.hasPeriodListTarget) return null;
    const id = crypto.randomUUID();
    const row = this.periodTemplateTarget.content.cloneNode(true).firstElementChild;
    if (!row) return null;
    // L'id de la période remplace `__ID__` dans TOUS les attributs du clone : noms
    // `plan_settings[periods][<id>][label|start|end]`, ids, `for`, `aria-*`, `data-plan-settings-period`.
    [row, ...row.querySelectorAll('*')].forEach((node) => {
      node.getAttributeNames().forEach((name) => {
        const value = node.getAttribute(name);
        if (value.includes(ID_PLACEHOLDER)) {
          node.setAttribute(name, value.replaceAll(ID_PLACEHOLDER, id));
        }
      });
    });
    return row;
  }

  // Brouillon : noms mis de côté, donc hors du corps posté et de l'état sale. Les champs restent ACTIVÉS (un
  // champ `disabled` ne se saisit pas, et le sélecteur de date écrit dans son champ ISO caché) : un modèle
  // rendu avec `disabled` est réactivé ici. Confirmé : noms rendus.
  #setDraftFields(row, draft) {
    row.querySelectorAll('input, select, textarea').forEach((field) => {
      const [from, to] = draft
        ? ['name', STASHED_NAME_ATTRIBUTE]
        : [STASHED_NAME_ATTRIBUTE, 'name'];
      const name = field.getAttribute(from);
      if (name) {
        field.setAttribute(to, name);
        field.removeAttribute(from);
      }
      field.disabled = false;
    });
  }

  #openEditor(row, { focus = false } = {}) {
    const editor = this.#editorOf(row);
    if (editor) editor.hidden = false;
    this.#editButtonOf(row)?.setAttribute('aria-expanded', 'true');
    if (focus) this.#labelField(row)?.focus();
  }

  #closeEditor(row) {
    const editor = this.#editorOf(row);
    if (editor) editor.hidden = true;
    this.#editButtonOf(row)?.setAttribute('aria-expanded', 'false');
  }

  #focusAfterRemoval(neighbour) {
    const target =
      (neighbour && this.#editButtonOf(neighbour)) || this.element.querySelector(ADD_SELECTOR);
    target?.focus();
  }

  // ── Lecture du DOM ────────────────────────────────────────────────────────────────────────────────────────

  #rows() {
    if (!this.hasPeriodListTarget) return [];
    return Array.from(this.periodListTarget.children).filter((node) =>
      node.hasAttribute(ROW_ATTRIBUTE)
    );
  }

  #rowId(row) {
    return row.getAttribute(ROW_ATTRIBUTE);
  }

  #rowOf(node) {
    if (!node || !this.hasPeriodListTarget) return null;
    const list = this.periodListTarget;
    let current = node;
    while (current && current.parentElement !== list) current = current.parentElement;
    return current?.hasAttribute?.(ROW_ATTRIBUTE) ? current : null;
  }

  #isDraft(row) {
    return row.hasAttribute(DRAFT_ATTRIBUTE);
  }

  // Un champ de la ligne par la fin de son nom (posé, ou mis de côté sur un brouillon).
  #field(row, key) {
    return row.querySelector(
      `input[name$="[${key}]"], input[${STASHED_NAME_ATTRIBUTE}$="[${key}]"]`
    );
  }

  #labelField(row) {
    return this.#field(row, 'label');
  }

  #displayOf(row, bound) {
    return this.#field(row, bound)?.closest(DATE_PICKER_ROOT)?.querySelector(DATE_PICKER_DISPLAY);
  }

  #periodOf(row) {
    return {
      label: String(this.#labelField(row)?.value ?? ''),
      start: String(this.#field(row, 'start')?.value ?? ''),
      end: String(this.#field(row, 'end')?.value ?? ''),
    };
  }

  // Les lignes confirmées aux dates valides, pour le motif « pendant « X » » des fériés.
  #activePeriods() {
    const fallback = this.#messages().default_label ?? '';
    return this.#rows()
      .filter((row) => !this.#isDraft(row))
      .map((row) => this.#periodOf(row))
      .filter(({ start, end }) => periodDateErrorKey(start, end) === null)
      .map((period) => ({
        ...period,
        displayLabel: period.label.trim() ? period.label : fallback,
      }));
  }

  #editButtonOf(row) {
    return row.querySelector(EDIT_SELECTOR);
  }

  #editorOf(row) {
    const id = this.#editButtonOf(row)?.getAttribute('aria-controls');
    return id ? row.querySelector(`[id="${id}"]`) : null;
  }

  #errorNode(row) {
    return row.querySelector(`[id="${row.id}_errors"]`);
  }

  #holidayRows() {
    return Array.from(this.element.querySelectorAll(HOLIDAY_SELECTOR), (node) => {
      let dates = [];
      try {
        const parsed = JSON.parse(node.getAttribute('data-dates') || '[]');
        if (Array.isArray(parsed)) dates = parsed;
      } catch {
        dates = [];
      }
      const box = node.querySelector('input[type="checkbox"]');
      return {
        node,
        key: node.getAttribute('data-plan-settings-holiday'),
        off: Boolean(box?.checked),
        dates,
      };
    });
  }

  #byId(id) {
    return this.element.querySelector(`[id="${id}"]`);
  }

  #form() {
    if (this.element.matches('form')) return this.element;
    return this.element.querySelector('form') ?? this.element.closest('form');
  }

  #messages() {
    return this.messagesValue || {};
  }

  #storedCalendar() {
    return (this.hasCalendarValue && this.calendarValue) || {};
  }

  // `null` (aucun lot daté) : Stimulus refuse `null` pour un Object, d'où la lecture gardée.
  #extent() {
    let extent = null;
    try {
      extent = this.hasExtentValue ? this.extentValue : null;
    } catch {
      extent = null;
    }
    if (!extent || !isValidIsoDate(extent.start) || !isValidIsoDate(extent.end)) return null;
    return { start: extent.start, end: extent.end };
  }

  #displayYear() {
    const year = this.hasDisplayYearValue ? this.displayYearValue : null;
    return Number.isInteger(year) && year > 0 ? year : null;
  }

  #setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  // ── Sale / propre, pied ───────────────────────────────────────────────────────────────────────────────────

  // Les paires (nom, valeur) que le formulaire posterait, hors champs techniques.
  #formState() {
    const form = this.#form();
    if (!form) return '[]';
    const pairs = [];
    form.querySelectorAll('input[name], select[name], textarea[name]').forEach((field) => {
      if (field.disabled || VOLATILE_FIELDS.has(field.name)) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      pairs.push([field.name, field.value]);
    });
    return JSON.stringify(pairs);
  }

  #syncSubmit() {
    const dirty = this.#formState() !== this.initialState;
    const errored = this.#rows().some((row) => this.#hasError(row));
    if (this.hasSubmitTarget) this.submitTarget.disabled = !dirty || errored;
    if (!this.hasStatusTarget) return;
    const status = this.statusTarget;
    const messages = this.#messages();
    const dot = status.querySelector(STATUS_DOT_SELECTOR);
    const text = status.querySelector(STATUS_TEXT_SELECTOR) ?? (dot ? null : status);
    this.#setText(text, (dirty ? messages.unsaved : messages.no_changes) ?? '');
    if (dot) dot.hidden = !dirty;
  }

  // ── Rendus venus du serveur ───────────────────────────────────────────────────────────────────────────────

  handleSubmitEnd() {
    this.#syncSubmit();
  }

  // Enveloppe le rendu de chaque flux Turbo : une fois le DOM remplacé, l'état posé par ce contrôleur revient.
  handleBeforeStreamRender(event) {
    const render = event.detail?.render;
    if (typeof render !== 'function') return;
    event.detail.render = async (stream) => {
      await render(stream);
      if (!this.connected) return;
      this.#syncInvalid();
      this.#syncSubmit();
    };
  }
}
